/**
 * The entities a case has resolved, and what each one rests on.
 *
 * **`GET /cases/{id}/entities` shipped at M4 with no screen.** The graph draws entities as
 * nodes, which answers "how are these connected" and not "what is this one, and why do we
 * think so" — so the read model that answers the second question had no way in.
 *
 * It is also where an analyst can say two entities are the same when the scorer could not
 * see it: a nickname, a transliteration, something learned outside the platform. That
 * **proposes**; it does not merge. The proposal joins the same queue, is decided by the
 * same endpoint, and produces the same audited decision as a scored one — ADR-0008's human
 * gate is not widened, and there is still no threshold anywhere that merges anything.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { AssertionResponse, EntityResponse, ManualMergeRequest } from '@/lib/schemas';
import {
  Badge,
  Button,
  Card,
  ConfidenceBadge,
  ErrorState,
  Loading,
  ProvenanceLink,
  Table,
} from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** The entities tab for one case. */
export function EntitiesTab({ caseId }: { caseId: string }): ReactElement {
  const canPropose = useCan('create_run_cases');
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [mergeWith, setMergeWith] = useState('');
  const [rationale, setRationale] = useState('');

  const entities = useQuery({
    queryKey: ['entities', caseId],
    queryFn: () => request<EntityResponse[]>(`/cases/${caseId}/entities`),
  });

  const propose = useMutation({
    // Takes the entity rather than reading `selected`, which is `string | null`. The panel
    // only renders for a selected entity, so it can never be null here — but that is a
    // fact about the render tree, not one the type can see, and the alternative was
    // sending `null` as an entity id if it ever stopped being true.
    mutationFn: (leftEntityId: string) =>
      request<unknown, ManualMergeRequest>(`/cases/${caseId}/merge-proposals`, {
        method: 'POST',
        body: {
          left_entity_id: leftEntityId,
          right_entity_id: mergeWith,
          rationale,
        },
      }),
    onSuccess: () => {
      setMergeWith('');
      setRationale('');
      void queryClient.invalidateQueries({ queryKey: ['proposals', caseId] });
      void queryClient.invalidateQueries({ queryKey: ['case-counts', caseId] });
    },
  });

  if (entities.isLoading) return <Loading />;
  if (entities.error) return <ErrorState error={entities.error} />;

  const rows = entities.data ?? [];
  const current = rows.find((entity) => entity.id === selected) ?? null;

  return (
    <div className="space-y-4">
      <Table
        headers={['Entity', 'Type', 'Claims', 'Subject', 'Generation', '']}
        empty="Resolution has not produced any entities for this case yet."
        rows={rows.map((entity) => [
          <span key="l" data-testid="entity-label" className="text-sm">
            {entity.canonical_label}
          </span>,
          <Badge key="t">{entity.entity_type}</Badge>,
          <span key="a" className="tabular-nums text-xs text-muted">
            {entity.assertion_count}
          </span>,
          entity.is_primary_subject ? (
            <Badge key="p" tone="accent">
              primary
            </Badge>
          ) : (
            <span key="p" className="text-muted">
              —
            </span>
          ),
          // The rebuild generation. An entity id is regenerated on every rebuild, so this
          // is the only stable answer to "is what I am looking at current".
          <span key="g" className="font-mono text-xs text-muted">
            {entity.rebuild_generation}
          </span>,
          <Button
            key="x"
            data-testid={`inspect-entity-${entity.id}`}
            onClick={() => setSelected(entity.id === selected ? null : entity.id)}
          >
            {entity.id === selected ? 'Hide' : 'Inspect'}
          </Button>,
        ])}
      />

      {current && (
        <>
          <EntityClaims caseId={caseId} entity={current} />

          {canPropose && (
            <Card title="Propose a merge">
              <div className="space-y-3 p-4">
                <p className="text-xs text-muted">
                  If you know this is the same person or thing as another entity — a
                  nickname, a transliteration, something you learned elsewhere — put the
                  pair in front of a reviewer. <strong>This proposes; it does not merge.</strong>{' '}
                  It joins the review queue and needs an approval like any scored proposal.
                </p>

                <div className="flex flex-wrap items-end gap-3">
                  <label className="block text-sm">
                    <span className="text-muted">Same as</span>
                    <select
                      data-testid="merge-with"
                      value={mergeWith}
                      onChange={(event) => setMergeWith(event.target.value)}
                      className="mt-1 block max-w-xs rounded border border-line bg-surface px-2 py-1.5 text-sm"
                    >
                      <option value="">Choose an entity…</option>
                      {rows
                        .filter((entity) => entity.id !== current.id)
                        .map((entity) => (
                          <option key={entity.id} value={entity.id}>
                            {entity.canonical_label}
                          </option>
                        ))}
                    </select>
                  </label>

                  <label className="block flex-1 text-sm">
                    <span className="text-muted">Why</span>
                    <input
                      data-testid="merge-rationale"
                      value={rationale}
                      onChange={(event) => setRationale(event.target.value)}
                      placeholder="What makes these the same?"
                      className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
                    />
                  </label>

                  <Button
                    data-testid="propose-merge"
                    tone="primary"
                    // A rationale is required: a scored proposal explains itself with its
                    // feature table, and a manual one has nothing else to offer a reviewer.
                    disabled={!mergeWith || rationale.trim().length === 0 || propose.isPending}
                    onClick={() => propose.mutate(current.id)}
                  >
                    {propose.isPending ? 'Proposing…' : 'Propose'}
                  </Button>
                </div>

                {propose.error != null && <ErrorState error={propose.error} />}
                {propose.isSuccess && (
                  <p role="status" data-testid="merge-proposed" className="text-sm text-ok">
                    Proposed. It is waiting in the Review tab for a decision.
                  </p>
                )}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The claims one entity rests on.
 *
 * Fetched by filtering the case's assertions rather than by a per-entity endpoint, because
 * the link table is what joins them and the read model does not publish it. Every row
 * keeps its provenance link, so an entity is never a claim you cannot follow back.
 *
 * @param props - The case and the entity to detail.
 * @returns The panel.
 */
function EntityClaims({
  caseId,
  entity,
}: {
  caseId: string;
  entity: EntityResponse;
}): ReactElement {
  const assertions = useQuery({
    queryKey: ['assertions', caseId, 'all-for-entity'],
    queryFn: () =>
      request<AssertionResponse[]>(`/cases/${caseId}/assertions`, { query: { limit: 200 } }),
  });

  if (assertions.isLoading) return <Loading />;
  if (assertions.error) return <ErrorState error={assertions.error} />;

  return (
    <Card title={`${entity.canonical_label} — ${entity.assertion_count} claims`}>
      <div className="p-4">
        <p className="mb-3 text-xs text-muted">
          What this entity is made of. Each claim keeps its own provenance: an entity is a
          projection over assertions, never a fact of its own.
        </p>
        <Table
          headers={['Claim', 'Value', 'Confidence', 'Source', 'Evidence']}
          empty="This entity has no claims, which should not happen."
          rows={(assertions.data ?? []).map((item) => [
            <span key="p" className="font-mono text-xs">
              {item.predicate}
            </span>,
            <span key="v" className="text-sm">
              {item.object_value}
            </span>,
            <ConfidenceBadge
              key="c"
              sourceReliability={item.confidence.source_reliability}
              informationCredibility={item.confidence.information_credibility}
              extractionConfidence={item.confidence.extraction_confidence}
            />,
            <span key="s" className="text-xs text-muted">
              {item.provenance.plugin_name}
            </span>,
            <ProvenanceLink key="e" caseId={caseId} assertionId={item.id} />,
          ])}
        />
      </div>
    </Card>
  );
}
